/**
 * Las dos preguntas de bienvenida y lo que se hace con ellas.
 *
 * Qué negocio tiene y qué quiere que haga su sitio. No se escriben en el prompt
 * —no son palabras suyas, y un texto que no escribió no se le pone delante—:
 * viajan aparte, como contexto para la IA (`brief` en `draftProjectWithAi`), y
 * en «Desde un template» ponen delante los de su industria. No se guardan en
 * la cuenta todavía (haría falta una columna): viven en este navegador, por
 * usuario. Se contestan una vez, pero no se dan por buenas para siempre: en
 * cada sitio nuevo se ven y se cambian debajo del prompt (`BriefChips`), y lo
 * guardado es lo último que usó.
 */

/**
 * Los tipos de negocio, en el orden en que se enseñan. Cubren las industrias de
 * los templates (`bestFor`) para que la respuesta sirva también para filtrarlos.
 * «Otro» pide escribirlo (`businessDetail`).
 */
export const WELCOME_BUSINESSES = [
  'services', 'restaurant', 'shop', 'wellness',
  'fitness', 'health', 'education', 'realestate',
  'events', 'hospitality', 'trades', 'portfolio',
  'creator', 'tech', 'nonprofit', 'other',
] as const
export type WelcomeBusiness = (typeof WELCOME_BUSINESSES)[number]

/** Lo más largo que se acepta al escribir el negocio en «Otro». */
export const WELCOME_DETAIL_MAX = 80

/* El nombre de cada uno. Los cinco primeros son los de las ideas de arranque
   del prompt, para que se llamen igual en las dos pantallas. */
export const WELCOME_BUSINESS_LABELS: Record<WelcomeBusiness, string> = {
  services: 'cwa.tpl.services',
  restaurant: 'cwa.tpl.restaurant',
  shop: 'cwa.tpl.shop',
  wellness: 'cwa.tpl.wellness',
  portfolio: 'cwa.tpl.portfolio',
  fitness: 'welcome.business.fitness',
  health: 'welcome.business.health',
  education: 'welcome.business.education',
  realestate: 'welcome.business.realestate',
  events: 'welcome.business.events',
  hospitality: 'welcome.business.hospitality',
  trades: 'welcome.business.trades',
  creator: 'welcome.business.creator',
  tech: 'welcome.business.tech',
  nonprofit: 'welcome.business.nonprofit',
  other: 'welcome.business.other',
}

/** Lo que el sitio tiene que hacer. Se pueden elegir varias. */
export const WELCOME_GOALS = ['bookings', 'services', 'products', 'blog', 'showcase'] as const
export type WelcomeGoal = (typeof WELCOME_GOALS)[number]

export type WelcomeAnswers = {
  business: WelcomeBusiness | null
  /** Lo que escribió al elegir «Otro», con sus palabras. */
  businessDetail?: string
  goals: WelcomeGoal[]
}

const keyFor = (userId: string) => `veltica:welcome:${userId}`

/** Si ya pasó por aquí —contestando u omitiendo— en este navegador. Sin usuario, sí: no se pregunta a ciegas. */
export function welcomeDone(userId: string | null | undefined): boolean {
  if (!userId) return true
  try {
    return globalThis.localStorage.getItem(keyFor(userId)) !== null
  } catch {
    // Sin almacenamiento, mejor no preguntar que preguntar cada vez.
    return true
  }
}

/** Lo contestado, o `null` si se omitió. */
export function saveWelcome(userId: string | null | undefined, answers: WelcomeAnswers | null): void {
  if (!userId) return
  try {
    globalThis.localStorage.setItem(keyFor(userId), JSON.stringify(answers ? { ...answers, at: Date.now() } : { skipped: true, at: Date.now() }))
  } catch {
    // Sin almacenamiento la pregunta vuelve la próxima vez. Molesto, no roto.
  }
}

/** Lo que contestó, o `null` si se omitió o todavía no pasó por aquí. */
export function readWelcome(userId: string | null | undefined): WelcomeAnswers | null {
  if (!userId) return null
  try {
    const raw = globalThis.localStorage.getItem(keyFor(userId))
    if (!raw) return null
    const saved = JSON.parse(raw) as Partial<WelcomeAnswers> & { skipped?: boolean }
    if (saved.skipped) return null
    const business = WELCOME_BUSINESSES.includes(saved.business as WelcomeBusiness) ? saved.business as WelcomeBusiness : null
    const goals = (saved.goals ?? []).filter((goal): goal is WelcomeGoal => WELCOME_GOALS.includes(goal))
    const detail = typeof saved.businessDetail === 'string' ? saved.businessDetail.trim().slice(0, WELCOME_DETAIL_MAX) : ''
    if (!business && !goals.length) return null
    return { business, goals, ...(business === 'other' && detail ? { businessDetail: detail } : {}) }
  } catch {
    return null
  }
}

/**
 * Lo elegido en otro sitio —las fichas de la caja de IA de veltica.com—, ya
 * comprobado: sólo negocios y objetivos de los de aquí. Llega como objeto o
 * como JSON en una dirección. `null` si no trae nada que valga.
 */
export function welcomeFrom(raw: unknown): WelcomeAnswers | null {
  let value = raw
  if (typeof value === 'string') {
    try { value = JSON.parse(value) } catch { return null }
  }
  if (!value || typeof value !== 'object') return null
  const given = value as { business?: unknown; goals?: unknown }
  const business = WELCOME_BUSINESSES.includes(given.business as WelcomeBusiness) && given.business !== 'other' ? given.business as WelcomeBusiness : null
  const goals = [...new Set(Array.isArray(given.goals) ? given.goals : [])].filter((goal): goal is WelcomeGoal => WELCOME_GOALS.includes(goal as WelcomeGoal))
  return business || goals.length ? { business, goals } : null
}

/** Lo que viaja a la IA: sólo lo contestado, sin huecos. */
export function welcomeBrief(answers: WelcomeAnswers | null): { business?: WelcomeBusiness; businessDetail?: string; goals?: WelcomeGoal[] } | undefined {
  if (!answers) return undefined
  const detail = answers.business === 'other' ? answers.businessDetail?.trim() : ''
  const brief = {
    ...(answers.business ? { business: answers.business } : {}),
    ...(detail ? { businessDetail: detail.slice(0, WELCOME_DETAIL_MAX) } : {}),
    ...(answers.goals.length ? { goals: answers.goals } : {}),
  }
  return Object.keys(brief).length ? brief : undefined
}

/**
 * Las industrias de los templates (`bestFor`) que encajan con cada negocio.
 * Un negocio de la bienvenida es más amplio que una industria de la
 * taxonomía, así que apunta a varias. «Otro» no filtra nada.
 */
export const WELCOME_INDUSTRIES: Record<WelcomeBusiness, readonly string[]> = {
  services: ['consulting', 'agency', 'finance'],
  restaurant: ['restaurant'],
  shop: ['ecommerce'],
  wellness: ['wellness'],
  fitness: ['fitness'],
  health: ['health'],
  education: ['education'],
  realestate: ['realestate'],
  events: ['events'],
  hospitality: ['hospitality'],
  trades: ['trades'],
  portfolio: ['portfolio', 'agency'],
  creator: ['creator'],
  tech: ['saas'],
  nonprofit: ['nonprofit'],
  other: [],
}

/** Si un template es para ese negocio, por sus industrias. Con «otro», todos. */
export function templateFitsBusiness(bestFor: readonly string[] | undefined, business: WelcomeBusiness): boolean {
  const industries = WELCOME_INDUSTRIES[business]
  if (!industries.length) return true
  return (bestFor ?? []).some((industry) => industries.includes(industry))
}
